import React from 'react'
import PropTypes from 'prop-types'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import MARKS from './marks'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import {
  Caption,
  Heading1Serif,
  Heading2Serif,
  Heading3Serif,
  Heading4Serif,
  Label,
  TextBody,
  TextBody2,
} from '@components/TextStyles'
import {
  ListItem,
  OrderedList,
  RichTextMain,
  TableRow,
  TableHead,
  TableCell,
  UnorderedList,
  Table,
  TableWrapper,
  ButtonContainer
} from './index.style'
import { GatsbyImage } from 'gatsby-plugin-image'
import PageTransitionLink from '@components/PageTransitionLink'
import InViewSection from '@components/InViewSection'
import Blockquote from '@components/TextStyles/Blockquote'
import Gallery from '@components/Gallery'
import Button from '@components/Button'
import HubspotForm from '@components/HubspotForm'
import IconArrowRight from '@svgs/IconArrowRight'
import Video from '@components/Video'
import AnimateSplitText from '@components/animation/AnimateSplitText'

const RichText = ({
  content,
  delay = 0,
  maxWidth,
  animate = true,
  paragraphSize = 'regular',
  article = false,
  buttonColor = 'charcoal',
  buildOn = false,
  as,
  enterFrom,
  enterTransparent,
  offset,
  duration,
  type,
  blankLinks,
  ...props
}) => {
  const getContent = children =>
    buildOn ? (
      <AnimateSplitText
        animate={animate}
        delay={delay}
        enterFrom={enterFrom}
        enterTransparent={enterTransparent}
        offset={offset}
        type={type}
        duration={duration}
      >
        {children}
      </AnimateSplitText>
    ) : (
      children
    )


  const options = {
    renderMark: {
      [MARKS.BOLD]: text => {
        if (!text[0]) return (text !== '' && text !== ' ') ? <strong>{text}</strong> : null
        if (text[0].type) return (text !== '' && text !== ' ') ? <strong>{text}</strong> : null
        const markup = React.Children.toArray(
          text[0].split(' ').map((str, strIndex) => (
            <>
              {strIndex > 0 ? ' ' : ''}
              <strong>{str}</strong>
            </>
          ))
        )
        return markup
      },
      [MARKS.ITALIC]: text => {
        if (!text[0]) return (text !== '' && text !== ' ') ? <em>{text}</em> : null
        if (text[0].type) return (text !== '' && text !== ' ') ? <em>{text}</em> : null
        const markup = React.Children.toArray(
            text[0].split(' ').map((str, strIndex) => (
              <>
                {strIndex > 0 ? ' ' : ''}
                <em>{str}</em>
              </>
            ))
        )
        return <em>{text}</em>
      },
      [MARKS.UNDERLINE]: text => <u>{text}</u>,
      [MARKS.SUBSCRIPT]: text => <sub>{text}</sub>,
      [MARKS.SUPERSCRIPT]: text => <sup>{text}</sup>,
      [MARKS.CODE]: text => <code>{text}</code>,
    },
    renderNode: {
      /*
       ** Blocks
       */

      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (children?.toString().trim() === '') return

        if (paragraphSize === 'regular') {
          return (
            <TextBody as={as} maxWidth={maxWidth}>
              {getContent(children)}
            </TextBody>
          )
        }

        if (paragraphSize === 'small') {
          return (
            <TextBody2 as={as} maxWidth={maxWidth}>
              {getContent(children)}
            </TextBody2>
          )
        }

        if (paragraphSize === 'heading1Serif') {
          return (
            <Heading1Serif as={as} maxWidth={maxWidth}>
              {getContent(children)}
            </Heading1Serif>
          )
        }

        if (paragraphSize === 'heading2Serif') {
          return (
            <Heading2Serif as={as} maxWidth={maxWidth}>
              {getContent(children)}
            </Heading2Serif>
          )
        }

        if (paragraphSize === 'heading3Serif') {
          return (
            <Heading3Serif as={as} maxWidth={maxWidth}>
              {getContent(children)}
            </Heading3Serif>
          )
        }

        if (paragraphSize === 'heading4Serif') {
          return (
            <Heading4Serif as={as} maxWidth={maxWidth}>
              {getContent(children)}
            </Heading4Serif>
          )
        }
      },

      [BLOCKS.HEADING_1]: (node, children) => (
        <Heading1Serif as="h1" maxWidth={maxWidth}>
          {getContent(children)}
        </Heading1Serif>
      ),

      [BLOCKS.HEADING_2]: (node, children) => (
        <Heading2Serif as="h2" maxWidth={maxWidth}>
          {getContent(children)}
        </Heading2Serif>
      ),

      [BLOCKS.HEADING_3]: (node, children) => (
        <Heading3Serif as={as ? as : "h3"} maxWidth={maxWidth}>
          {getContent(children)}
        </Heading3Serif>
      ),

      [BLOCKS.HEADING_4]: (node, children) => (
        <Heading4Serif>{getContent(children)}</Heading4Serif>
      ),

      [BLOCKS.HEADING_5]: (node, children) => (
        <Caption>{getContent(children)}</Caption>
      ),

      [BLOCKS.HEADING_6]: (node, children) => (
        <Label uppercase={false}>{getContent(children)}</Label>
      ),


      [BLOCKS.OL_LIST]: (node, children) => (
        <OrderedList>{children}</OrderedList>
      ),

      [BLOCKS.UL_LIST]: (node, children) => (
        <UnorderedList>{children}</UnorderedList>
      ),

      [BLOCKS.LIST_ITEM]: (node, children) => {
        return (
          <InViewSection>
            <ListItem>{children}</ListItem>
          </InViewSection>
        )
      },

      [BLOCKS.TABLE]: (node, children) => {
        return (
          <TableWrapper>
            <Table>
              <tbody>{children}</tbody>
            </Table>
          </TableWrapper>
        )
      },
      [BLOCKS.TABLE_ROW]: (node, children) => <TableRow>{children}</TableRow>,
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <TableHead>{children}</TableHead>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => (
        <TableCell>{children}</TableCell>
      ),

      [BLOCKS.HR]: () => <hr />,

      [BLOCKS.QUOTE]: (node, children) => <Blockquote>{children}</Blockquote>,

      [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
        const { description, gatsbyImageData } = node.data.target

        return <GatsbyImage image={gatsbyImageData} alt={description} />
      },

      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const block = node.data.target

        switch (block.__typename) {
          case 'ContentfulVimeoVideo':
            return <Video content={block} className="video"/>

          case 'ContentfulYouTubeVideo':
            return <Video content={block} className="video"/>

          case 'ContentfulGallery':
            return (
              <Gallery
                images={block.images}
                largeFirst={block.largeImageFirst}
              />
            )

          case 'ContentfulInternalLink':
            return (
              <Button
                type="internalLink"
                variant="outline"
                to={block.page.slug ? block.page.slug : ''}
                iconRight={<IconArrowRight />}
                color={buttonColor}
              >
                {block.text}
              </Button>
            )
          case 'ContentfulHubspotForm':
            return (
              <HubspotForm content={block}/>
            )

          case 'ContentfulExternalLink':
            return (
              <ButtonContainer className="button-container">
                <Button
                  type="externalLink"
                  variant="outline"
                  href={block.url}
                  color={buttonColor}
                >
                  {block.text}
                </Button>
              </ButtonContainer>
            )

          default:
            return null
        }
      },

      /*
       ** Inlines
       */

      [INLINES.HYPERLINK]: (node, children) => {
        if(node.data.uri.startsWith("#")) {
          const scrollTos = (e) => {
            const _ele = document.getElementById(e.target.getAttribute('data-ref').replace('#', ''));
            window.scrollTo({
              top: _ele.offsetTop,
              left: 0,
              behavior: "smooth",
            });
          }
          return (
            <button type="button" onClick={scrollTos} data-ref={node.data.uri}>{children}</button>
          )
        }
        return (
          <>
            { node.data.uri.includes('https://bravebison.com') ? blankLinks ?
              <a href={node.data.uri} target="_blank">
                {children}
              </a>
              :
              <PageTransitionLink to={node.data.uri.replace('https://bravebison.com/', '')}>
                {children}
              </PageTransitionLink>
            :
              <a href={node.data.uri} target="_blank" rel="noreferrer">
                {children}
              </a>
            }
          </>
        )
      }
      ,

      [INLINES.ASSET_HYPERLINK]: (node, children) => (
        <a href={node.data.target.file.url}>{children}</a>
      ),

      [INLINES.ENTRY_HYPERLINK]: (node, children) => (
        <PageTransitionLink to={node.data.target.slug}>
          {children}
        </PageTransitionLink>
      ),

      // [INLINES.EMBEDDED_ENTRY]: (node, children) => {},
    },
    renderText: text =>
      React.Children.toArray(
        text.split(/(?:\\n|\n)+/).map((t, i) =>
          text.length === 0 && t === '' && i ? (
            <>{(i !== text.length -1)  && <br />}</>
          ) : i > 0 ? (
            <>
              <br />
              {t}
            </>
          ) : (
            <>
            {t}
            </>
          )
        )
      ),
  }

  return (
    <>
      {article ? (
        renderRichText(content, options)
      ) : (
        <RichTextMain {...props}>
          {renderRichText(content, options)}
        </RichTextMain>
      )}
    </>
  )
}

RichText.propTypes = {
  content: PropTypes.object,
  delay: PropTypes.number,
  animate: PropTypes.bool,
  article: PropTypes.bool,
  buildOn: PropTypes.bool,
  maxWidth: PropTypes.number,
  as: PropTypes.string,
  paragraphSize: PropTypes.oneOf([
    'regular',
    'small',
    'heading1Serif',
    'heading2Serif',
    'heading3Serif',
    'heading4Serif',
  ]),
  enterFrom: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  enterTransparent: PropTypes.bool,
  offset: PropTypes.string,
  duration: PropTypes.number,
  buttonColor: PropTypes.string,
}

export default RichText
